import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { LoadingSpinner } from '../../../shared/ui';
import {
  AdminBlogShell,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  fetchYowanAdminNotificationAudienceGroupList,
  fetchYowanAdminNotificationPolicyDetail,
  fetchYowanAdminNotificationPolicyList,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const readOnlyGridStyle = {
  display: 'grid',
  gap: '0.85rem',
};

const tableStyle = {
  minWidth: '72rem',
};

const groupColumnStyle = {
  width: '15rem',
  minWidth: '15rem',
};

const kindColumnStyle = {
  width: '7rem',
  minWidth: '7rem',
  whiteSpace: 'nowrap',
};

const descriptionColumnStyle = {
  minWidth: '22rem',
};

const actionColumnStyle = {
  width: '12rem',
  minWidth: '12rem',
};

const readOnlyFieldStyle = {
  cursor: 'not-allowed',
};

const readOnlyControlStyle = {
  cursor: 'not-allowed',
};

const getAudienceProjection = (audience, audienceGroupMap) =>
  audienceGroupMap[audience.audienceGroupKey] || null;

const getAudienceGroupHref = (audience, projection) => (
  projection?.groupHref ||
  `/admin/notifications/recipients?tab=groups&group_key=${encodeURIComponent(audience.audienceGroupKey)}`
);

const getRecipientListHref = (audience, projection) => {
  if (projection?.recipientListHref) {
    return projection.recipientListHref;
  }

  if (audience.audienceGroupKey === 'yowan_news_recipients') {
    return '/admin/notifications/recipients?tab=list&category_key=news';
  }

  if (audience.audienceGroupKey === 'yowan_blog_recipients') {
    return '/admin/notifications/recipients?tab=list&category_key=blog';
  }

  return '';
};

const canLinkToRecipientList = (audience, projection) =>
  projection?.canListRecipients === true ||
  audience.audienceGroupKey === 'yowan_news_recipients' ||
  audience.audienceGroupKey === 'yowan_blog_recipients';

const getAudienceOperationNote = (audience, projection) => {
  if (projection?.recipientVisibility === 'preference_category' || canLinkToRecipientList(audience, projection)) {
    return '通知宛先リストで現在の候補と受信設定を確認できます。';
  }

  if (projection?.recipientVisibility === 'dynamic_access_role' || audience.audienceGroupKey === 'operator_admin') {
    return '管理者権限をもとに送信時に動的解決されます。';
  }

  if (
    projection?.recipientVisibility === 'contextual' ||
    audience.audienceGroupKey === 'auth_subject_self' ||
    audience.audienceGroupKey === 'applicant_self'
  ) {
    return 'イベント context をもとに送信時に動的解決されます。';
  }

  if (projection?.recipientVisibility === 'explicit_config') {
    return '明示設定された宛先定義をもとに送信時に解決されます。';
  }

  return '実際に解決された宛先は通知履歴詳細で確認します。';
};

const ReadOnlyField = ({ label, value, multiline = false, rows = 4 }) => {
  const fieldValue = value || '-';

  return (
    <label className="c-form__field" style={readOnlyFieldStyle}>
      <span className="c-form__label">{label}</span>
      {multiline ? (
        <textarea
          className="c-form__control c-form__control--textarea"
          readOnly
          rows={rows}
          style={readOnlyControlStyle}
          value={fieldValue}
        />
      ) : (
        <input
          className="c-form__control"
          readOnly
          style={readOnlyControlStyle}
          type="text"
          value={fieldValue}
        />
      )}
    </label>
  );
};

const AdminNotificationPolicyDetailPage = () => {
  const content = yowanPageContent['/admin/notifications/policies/:eventKey'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const { eventKey = '' } = useParams();
  const [record, setRecord] = useState(null);
  const [policyRows, setPolicyRows] = useState([]);
  const [audienceGroupMap, setAudienceGroupMap] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setLoadError('');

    Promise.all([
      fetchYowanAdminNotificationPolicyDetail(eventKey),
      fetchYowanAdminNotificationPolicyList(),
      fetchYowanAdminNotificationAudienceGroupList({ pageSize: 100 }).catch(() => ({ results: [] })),
    ])
      .then(([payload, listPayload, audienceGroupPayload]) => {
        if (isMounted) {
          setRecord(payload);
          setPolicyRows(listPayload.results);
          setAudienceGroupMap(
            audienceGroupPayload.results.reduce((accumulator, row) => {
              accumulator[row.groupKey] = row;
              return accumulator;
            }, {}),
          );
        }
      })
      .catch(() => {
        if (isMounted) {
          setRecord(null);
          setPolicyRows([]);
          setAudienceGroupMap({});
          setLoadError('通知ルールを読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [eventKey]);

  if (!content) {
    return null;
  }

  const backToPolicies = appendYowanPreviewQueryIfNeeded('/admin/notifications/policies', preservedDomainSearch);
  const backToDispatches = appendYowanPreviewQueryIfNeeded('/admin/notifications', preservedDomainSearch);
  const backToRecipients = appendYowanPreviewQueryIfNeeded('/admin/notifications/recipients', preservedDomainSearch);
  const sortedPolicyRows = [...policyRows].sort((left, right) =>
    left.eventName.localeCompare(right.eventName, 'ja'),
  );
  const currentPolicyIndex = sortedPolicyRows.findIndex((row) => row.eventKey === eventKey);
  const previousPolicy = currentPolicyIndex > 0 ? sortedPolicyRows[currentPolicyIndex - 1] : null;
  const nextPolicy = currentPolicyIndex >= 0 && currentPolicyIndex < sortedPolicyRows.length - 1
    ? sortedPolicyRows[currentPolicyIndex + 1]
    : null;

  if (!record) {
    return (
      <AdminBlogShell activeNavHref={content.activeNavHref} routeKey={content.routeKey}>
        <AdminPageHeader
          title={content.header.title}
          paragraphs={isLoading ? [] : [loadError || '指定された通知ルールは見つかりませんでした。']}
          actions={[
            <Link key="back-to-policies" to={backToPolicies}>
              通知ルールへ戻る
            </Link>,
          ]}
        />
        {isLoading ? <LoadingSpinner /> : null}
      </AdminBlogShell>
    );
  }

  return (
    <AdminBlogShell activeNavHref={content.activeNavHref} routeKey={content.routeKey}>
      <AdminPageHeader
        title={`${record.eventName} / 通知ルール`}
        paragraphs={content.header.paragraphs}
        actions={[
          previousPolicy ? (
            <Link
              key="previous-policy"
              to={appendYowanPreviewQueryIfNeeded(previousPolicy.detailHref, preservedDomainSearch)}
            >
              Prev
            </Link>
          ) : null,
          nextPolicy ? (
            <Link
              key="next-policy"
              to={appendYowanPreviewQueryIfNeeded(nextPolicy.detailHref, preservedDomainSearch)}
            >
              Next
            </Link>
          ) : null,
          <Link key="back-to-policies" to={backToPolicies}>
            通知ルールへ戻る
          </Link>,
          <Link key="back-to-dispatches" to={backToDispatches}>
            通知履歴へ
          </Link>,
          <Link key="back-to-recipients" to={backToRecipients}>
            通知宛先へ
          </Link>,
        ]}
      />

      <section className="yowan-admin-form c-surface-content" style={readOnlyGridStyle}>
        <ReadOnlyField label="通知種別キー" value={record.eventKey} />
        <ReadOnlyField label="カテゴリ" value={record.categoryLabel} />
        <ReadOnlyField label="イベント名" value={record.eventName} />
        <ReadOnlyField label="発生タイミング" value={record.timingLabel} />
        <ReadOnlyField label="既定の判断" value={record.defaultActionLabel} />
        <ReadOnlyField label="現在の送信判断" value={record.decisionStatusLabel} />
        <ReadOnlyField label="送信方法" value={`${record.channelLabel} / ${record.deliveryModeLabel}`} />
        <ReadOnlyField label="テンプレート" value={record.templateKey} />
        <ReadOnlyField label="発生条件" value={record.triggerPoint} />
        <ReadOnlyField label="宛先概要" value={record.audienceSummary} />
        <ReadOnlyField label="補足" value={record.notes} multiline rows={4} />
        <ReadOnlyField label="設定JSON" value={record.configJson} multiline rows={8} />
      </section>

      <section className="yowan-admin-stack">
        <AdminPageHeader
          title="宛先グループ"
          paragraphs={[
            'この通知ルールが参照する宛先グループと解決方法を確認します。具体的な実送信宛先は通知履歴詳細で確認します。',
          ]}
        />
        <div className="yowan-admin-resource-table-wrap c-surface-content">
          <table className="yowan-admin-resource-table" style={tableStyle}>
            <thead>
              <tr>
                <th scope="col" style={groupColumnStyle}>グループ</th>
                <th scope="col" style={kindColumnStyle}>種別</th>
                <th scope="col" style={descriptionColumnStyle}>対象条件</th>
                <th scope="col" style={actionColumnStyle}>確認</th>
              </tr>
            </thead>
            <tbody>
              {record.audiences.map((audience, index) => {
                const projection = getAudienceProjection(audience, audienceGroupMap);
                const recipientListHref = getRecipientListHref(audience, projection);
                const audienceGroupHref = getAudienceGroupHref(audience, projection);

                return (
                  <tr key={audience.id || `${audience.audienceGroupKey}-${index}`}>
                    <td style={groupColumnStyle}>
                      <p className="yowan-admin-resource-table__title">
                        {projection?.groupLabel || audience.audienceGroupLabel || '-'}
                      </p>
                      <p className="yowan-admin-resource-table__subline">{audience.audienceGroupKey}</p>
                    </td>
                    <td style={kindColumnStyle}>
                      <p className="yowan-admin-resource-table__title">
                        {projection?.groupKind || audience.audienceGroupKind || '-'}
                      </p>
                      <p className="yowan-admin-resource-table__subline">
                        {projection?.recipientVisibilityLabel || '-'}
                      </p>
                    </td>
                    <td style={descriptionColumnStyle}>
                      <p className="yowan-admin-resource-table__title">
                        {projection?.description || audience.description || '-'}
                      </p>
                      <p className="yowan-admin-resource-table__subline">
                        {projection?.resolutionSummary || audience.resolutionSummary || '-'}
                      </p>
                      <p className="yowan-admin-resource-table__subline">
                        {getAudienceOperationNote(audience, projection)}
                      </p>
                    </td>
                    <td style={actionColumnStyle}>
                      <div className="yowan-admin-resource-table__actions">
                        <Link to={appendYowanPreviewQueryIfNeeded(audienceGroupHref, preservedDomainSearch)}>
                          グループ
                        </Link>
                        {canLinkToRecipientList(audience, projection) && recipientListHref ? (
                          <Link to={appendYowanPreviewQueryIfNeeded(recipientListHref, preservedDomainSearch)}>
                            宛先リスト
                          </Link>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!record.audiences.length ? (
                <tr>
                  <td colSpan="4">宛先グループは設定されていません。</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>
    </AdminBlogShell>
  );
};

export default AdminNotificationPolicyDetailPage;
